import Phaser from 'phaser';
import type { MissionView } from '../systems/MissionSystem';

export class HudScene extends Phaser.Scene {
  private cashText!: Phaser.GameObjects.Text;
  private starsText!: Phaser.GameObjects.Text;
  private hpBar!: Phaser.GameObjects.Graphics;
  private missionText!: Phaser.GameObjects.Text;
  private areaText!: Phaser.GameObjects.Text;
  private fpsText!: Phaser.GameObjects.Text;
  private arrow!: Phaser.GameObjects.Triangle;
  private arrowDistText!: Phaser.GameObjects.Text;
  private state = { hp: 100, hpMax: 100, cash: 0, stars: 0, area: '', mission: '', fps: 0 };

  constructor() { super({ key: 'HudScene', active: false }); }

  create() {
    this.cashText = this.add.text(this.scale.width - 16, 12, '£0', {
      fontSize: '22px', color: '#9be29b', fontStyle: 'bold'
    }).setOrigin(1, 0);

    this.starsText = this.add.text(this.scale.width - 16, 40, '', {
      fontSize: '20px', color: '#ffd84a'
    }).setOrigin(1, 0);

    this.hpBar = this.add.graphics();

    this.missionText = this.add.text(16, 12, '', {
      fontSize: '14px', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)', padding: { left: 8, right: 8, top: 4, bottom: 4 },
      wordWrap: { width: 320 }
    });

    this.areaText = this.add.text(16, this.scale.height - 28, '', {
      fontSize: '16px', color: '#ddd', fontStyle: 'italic'
    });

    this.fpsText = this.add.text(this.scale.width - 16, this.scale.height - 22, '', {
      fontSize: '11px', color: '#666'
    }).setOrigin(1, 0);

    // Off-screen mission direction arrow
    this.arrow = this.add.triangle(0, 0,
      0, -18, -14, 14, 14, 14,
      0xffe24a, 1
    ).setStrokeStyle(2, 0x000000, 1).setVisible(false).setDepth(100);
    this.arrowDistText = this.add.text(0, 0, '', {
      fontSize: '12px', color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)',
      padding: { left: 4, right: 4, top: 1, bottom: 1 }
    }).setOrigin(0.5, 0.5).setVisible(false).setDepth(100);

    this.scale.on('resize', () => this.layout());
    this.layout();

    // Listen to GameScene events
    const game = this.scene.get('GameScene');
    game.events.on('hud:cash',    (n: number) => { this.state.cash = n; this.draw(); });
    game.events.on('hud:hp',      (hp: number, max: number) => { this.state.hp = hp; this.state.hpMax = max; this.draw(); });
    game.events.on('hud:stars',   (n: number) => { this.state.stars = n; this.draw(); });
    game.events.on('hud:area',    (s: string) => { this.state.area = s; this.draw(); });
    game.events.on('hud:mission', (m: MissionView) => {
      this.state.mission = m.timerLeft != null
        ? `${m.text}\n⏱ ${Math.ceil(m.timerLeft)}s`
        : m.text;
      this.draw();
    });
    game.events.on('hud:arrow', (data: { angle: number; distance: number } | null) => {
      if (!data) {
        this.arrow.setVisible(false);
        this.arrowDistText.setVisible(false);
        return;
      }
      const w = this.scale.width, h = this.scale.height;
      const cx = w / 2, cy = h / 2;
      const margin = 50;
      const halfW = cx - margin, halfH = cy - margin;
      const cos = Math.cos(data.angle), sin = Math.sin(data.angle);
      const tx = cos !== 0 ? halfW / Math.abs(cos) : Infinity;
      const ty = sin !== 0 ? halfH / Math.abs(sin) : Infinity;
      const t = Math.min(tx, ty);
      const ax = cx + cos * t;
      const ay = cy + sin * t;
      this.arrow.setPosition(ax, ay).setRotation(data.angle + Math.PI / 2).setVisible(true);
      // Distance label, nudged toward screen centre so it stays on screen
      this.arrowDistText
        .setPosition(ax - cos * 24, ay - sin * 24)
        .setText(`${Math.round(data.distance / 32)}m`)
        .setVisible(true);
    });
    this.draw();
  }

  private layout() {
    this.cashText.setPosition(this.scale.width - 16, 12);
    this.starsText.setPosition(this.scale.width - 16, 40);
    this.areaText.setPosition(16, this.scale.height - 28);
    this.fpsText.setPosition(this.scale.width - 16, this.scale.height - 22);
    this.draw();
  }

  update() {
    this.state.fps = Math.round(this.game.loop.actualFps);
    this.fpsText.setText(`${this.state.fps} fps`);
  }

  private draw() {
    this.cashText.setText(`£${this.state.cash.toString().padStart(4, '0')}`);
    this.starsText.setText('★'.repeat(this.state.stars) + '☆'.repeat(Math.max(0, 5 - this.state.stars)));
    this.missionText.setText(this.state.mission || '');
    this.areaText.setText(this.state.area);
    this.hpBar.clear();
    const w = 160, h = 10;
    const x = this.scale.width - 16 - w, y = 70;
    this.hpBar.fillStyle(0x000000, 0.5).fillRect(x - 2, y - 2, w + 4, h + 4);
    this.hpBar.fillStyle(0x222222, 1).fillRect(x, y, w, h);
    const pct = Math.max(0, this.state.hp) / this.state.hpMax;
    const col = pct > 0.5 ? 0x4dd14d : pct > 0.25 ? 0xddc04a : 0xd64a4a;
    this.hpBar.fillStyle(col, 1).fillRect(x, y, w * pct, h);
  }
}
